import { useState } from "react";
import type { WeightDisplayUnit } from "@medassist/shared";
import { DEFAULT_WEIGHT_UNIT, formatWeight, kgToDisplay, displayToKg } from "@medassist/shared";
import { savePatientProfile, ApiError } from "../../../lib/api.js";
import type { WidgetProps } from "../types.js";
import { Select } from "../../Select.js";

export function WeightWidget({ patient, onPatientUpdated, onError }: WidgetProps) {
  const weightUnit: WeightDisplayUnit = patient.preferredWeightUnit ?? DEFAULT_WEIGHT_UNIT;
  const [weightEditing,   setWeightEditing]   = useState(false);
  const [weightInput,     setWeightInput]     = useState("");
  const [weightUnitInput, setWeightUnitInput] = useState<WeightDisplayUnit>(weightUnit);
  const [weightBusy,      setWeightBusy]      = useState(false);

  async function saveWeight() {
    const v = parseFloat(weightInput);
    if (!isFinite(v) || v <= 0) return;
    setWeightBusy(true);
    onError(null);
    try {
      const updated = await savePatientProfile(patient.patientId, {
        weightKg: displayToKg(v, weightUnitInput),
        preferredWeightUnit: weightUnitInput,
      });
      onPatientUpdated(updated);
      setWeightEditing(false);
    } catch (err) {
      onError(err instanceof ApiError ? err.message : "Couldn't save weight");
    } finally {
      setWeightBusy(false);
    }
  }

  return (
    <section className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
      <h2 className="mb-2 font-semibold text-gray-900 dark:text-gray-100">Weight</h2>
      {weightEditing ? (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <input
              type="number" inputMode="decimal" step="any" autoFocus
              value={weightInput}
              onChange={(e) => setWeightInput(e.target.value)}
              className="w-24 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-2 py-1 text-sm text-gray-900 dark:text-gray-100"
            />
            <Select
              value={weightUnitInput}
              onChange={(e) => setWeightUnitInput(e.target.value as WeightDisplayUnit)}
            >
              <option value="lbs">lbs</option>
              <option value="kg">kg</option>
            </Select>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => void saveWeight()}
              disabled={weightBusy}
              className="rounded-md bg-(--accent) px-3 py-1 text-sm font-medium text-(--accent-contrast) hover:bg-(--accent-hover) disabled:opacity-50"
            >
              {weightBusy ? "Saving…" : "Save"}
            </button>
            <button
              onClick={() => setWeightEditing(false)}
              className="rounded-md border border-gray-300 dark:border-gray-700 px-3 py-1 text-sm text-gray-700 dark:text-gray-300"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-between">
          <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
            {patient.weightKg != null ? formatWeight(patient.weightKg, weightUnit) : "—"}
          </p>
          <button
            onClick={() => {
              setWeightInput(patient.weightKg != null ? String(kgToDisplay(patient.weightKg, weightUnit)) : "");
              setWeightUnitInput(weightUnit);
              setWeightEditing(true);
            }}
            className="text-xs text-(--accent) hover:underline"
          >
            {patient.weightKg != null ? "Update" : "Add"}
          </button>
        </div>
      )}
    </section>
  );
}
